import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OuraNav from "../components/OuraNav";
import fetchBase from "../utils/fetchBase";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableSectionTile({ section, hubId, renamingId, renameValue, setRenamingId, setRenameValue, handleRenameSection, handleDeleteSection, navigate }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: "#fff",
        borderRadius: "14px",
        padding: "24px 20px",
        border: "1px solid #e0e0e0",
        cursor: "grab",
        position: "relative",
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.18)" : "0 1px 4px rgba(0,0,0,0.06)",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={() => !isDragging && navigate(`/document-hub/${hubId}/sections/${section.id}`)}
        >
            <div style={{ fontSize: "28px", marginBottom: "12px" }}>📁</div>
            {renamingId === section.id ? (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleRenameSection(section.id)}
                        onPointerDown={e => e.stopPropagation()}
                        style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "13px" }}
                    />
                    <button onPointerDown={e => e.stopPropagation()} onClick={() => handleRenameSection(section.id)} style={{ background: "#1d1d1f", color: "#fff", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", cursor: "pointer" }}>Save</button>
                    <button onPointerDown={e => e.stopPropagation()} onClick={() => setRenamingId(null)} style={{ background: "none", border: "1px solid #ccc", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", cursor: "pointer" }}>✕</button>
                </div>
            ) : (
                <p
                    style={{ fontWeight: "600", fontSize: "14px", color: "#1d1d1f", margin: 0 }}
                    onPointerDown={e => e.stopPropagation()}
                    onDoubleClick={e => { e.stopPropagation(); setRenamingId(section.id); setRenameValue(section.name); }}
                >
                    {section.name}
                </p>
            )}
            <button
                onPointerDown={e => e.stopPropagation()}
                onClick={(e) => handleDeleteSection(e, section.id)}
                style={{
                    position: "absolute", top: "10px", right: "10px",
                    background: "none", border: "none", color: "#ff3b30",
                    fontSize: "13px", cursor: "pointer", fontWeight: "700"
                }}
            >
                ✕
            </button>
        </div>
    );
}

export default function DocumentSectionPage() {
    const { hubId } = useParams();
    const [hub, setHub] = useState(null);
    const [sections, setSections] = useState([]);
    const [newSectionName, setNewSectionName] = useState("");
    const [showInput, setShowInput] = useState(false);
    const [loading, setLoading] = useState(true);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const navigate = useNavigate();
    const sectionsRef = useRef([]);
    const orderChangedRef = useRef(false);


    const token = localStorage.getItem("jwt_token");
    const authHeader = `Bearer ${token}`;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

        const handleDragEnd = (event) => {
            const { active, over } = event;
            if (active.id !== over?.id) {
                setSections(items => {
                    const oldIndex = items.findIndex(s => s.id === active.id);
                    const newIndex = items.findIndex(s => s.id === over.id);
                    const reordered = arrayMove(items, oldIndex, newIndex);
                    sectionsRef.current = reordered;
                    orderChangedRef.current = true;
                    return reordered;
                });
            }
        };


                const fetchSections = async () => {
                    try {
                        const [hubRes, sectionsRes] = await Promise.all([
                            fetch(`${fetchBase}/api/document-hub/hubs/${hubId}`, {
                                headers: { Authorization: authHeader }
                            }),
                            fetch(`${fetchBase}/api/document-hub/hubs/${hubId}/sections`, {
                                headers: { Authorization: authHeader }
                            })
                        ]);
                        const hubData = await hubRes.json();
                        const sectionsData = await sectionsRes.json();
                        setHub(hubData);
                        if (Array.isArray(sectionsData)) {
                            setSections(sectionsData);
                            sectionsRef.current = sectionsData;
                            orderChangedRef.current = false;
                        }
                    } catch (err) {
                        console.error("Failed to fetch sections:", err);
                    } finally {
                        setLoading(false);
                    }
                };

            const saveSectionOrder = () => {
                if (!orderChangedRef.current) return;
                const orderedIds = sectionsRef.current.map(s => s.id);
                fetch(`${fetchBase}/api/document-hub/hubs/${hubId}/sections/reorder`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader
                    },
                    body: JSON.stringify(orderedIds),
                    keepalive: true
                }).catch(err => console.error("Failed to save section order:", err));
                orderChangedRef.current = false;
            };

            useEffect(() => {
                fetchSections();

                const handleBeforeUnload = () => {
                    saveSectionOrder();
                };
                window.addEventListener("beforeunload", handleBeforeUnload);

                return () => {
                    saveSectionOrder();
                    window.removeEventListener("beforeunload", handleBeforeUnload);
                };
            }, [hubId]);


    const handleCreateSection = async () => {
        if (!newSectionName.trim()) return;
        try {
            const res = await fetch(`${fetchBase}/api/document-hub/hubs/${hubId}/sections`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authHeader
                },
                body: JSON.stringify({ name: newSectionName.trim() })
            });
            if (res.ok) {
                setNewSectionName("");
                setShowInput(false);
                fetchSections();
            }
        } catch (err) {
            console.error("Failed to create section:", err);
        }
    };

    const handleRenameSection = async (id) => {
        if (!renameValue.trim()) return;
        try {
            const res = await fetch(`${fetchBase}/api/document-hub/sections/${id}/rename`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: authHeader
                },
                body: JSON.stringify({ name: renameValue.trim() })
            });
            if (res.ok) {
                setRenamingId(null);
                setRenameValue("");
                fetchSections();
            }
        } catch (err) {
            console.error("Failed to rename section:", err);
        }
    };

    const handleDeleteSection = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Delete this section and all its documents?")) return;
        try {
            await fetch(`${fetchBase}/api/document-hub/sections/${id}`, {
                method: "DELETE",
                headers: { Authorization: authHeader }
            });
            fetchSections();
        } catch (err) {
            console.error("Failed to delete section:", err);
        }
    };

    return (
        <div style={{ backgroundColor: "#f2f2f7", minHeight: "100vh" }}>
            <OuraNav
                links={[
                    { label: "Dashboard", href: "/admin-dashboard" },
                    { label: "Employees", href: "/employees" },
                    { label: "Clients", href: "/admin/clients" },
                    { label: "Scheduler", href: "/scheduler" },
                    { label: "Payroll", href: "/admin/payroll" }
                ]}
            />

            <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 40px" }}>

                {/* Breadcrumb — "Breadcrumb navigation back to the Hub list" */}
                <div style={{ fontSize: "13px", color: "#6e6e73", marginBottom: "24px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                        onClick={() => navigate("/document-hub")}
                        style={{ cursor: "pointer", color: "#0071e3", fontWeight: "500" }}
                    >
                        Document Hub
                    </span>
                    <span>›</span>
                    <span style={{ color: "#1d1d1f", fontWeight: "500" }}>{hub?.name || "Sections"}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
                    <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#1d1d1f", margin: 0 }}>
                        {hub?.name || "Sections"}
                    </h1>
                    {/* "Button to create a new Section (admin enters a custom name)" */}
                    <button
                        onClick={() => setShowInput(true)}
                        style={{
                            padding: "9px 20px",
                            backgroundColor: "#1d1d1f",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer"
                        }}
                    >
                        + New Section
                    </button>
                </div>

                {showInput && (
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "16px 20px",
                        marginBottom: "24px",
                        border: "1px solid #e0e0e0",
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                        <input
                            type="text"
                            placeholder='Section name e.g. "Admission Packet"'
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleCreateSection()}
                            autoFocus
                            style={{
                                flex: 1,
                                padding: "9px 14px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                                outline: "none",
                                color: "#1d1d1f",
                                backgroundColor: "#ffffff"
                            }}
                        />
                        <button
                            onClick={handleCreateSection}
                            style={{
                                padding: "9px 20px",
                                backgroundColor: "#1d1d1f",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "13px",
                                cursor: "pointer"
                            }}
                        >
                            Create
                        </button>
                        <button
                            onClick={() => { setShowInput(false); setNewSectionName(""); }}
                            style={{
                                padding: "9px 14px",
                                backgroundColor: "transparent",
                                color: "#6e6e73",
                                border: "1px solid #ccc",
                                borderRadius: "6px",
                                fontSize: "13px",
                                cursor: "pointer"
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {/* "Admin sees all sections as tiles" */}
                {loading ? (
                    <p style={{ color: "#6e6e73", fontSize: "14px" }}>Loading...</p>
                ) : sections.length === 0 ? (
                    <p style={{ color: "#6e6e73", fontSize: "14px" }}>No sections yet. Create one to get started.</p>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={sections.map(s => s.id)} strategy={rectSortingStrategy}>
                          <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: "20px"
                          }}>
                              {sections.map(section => (
                                  <SortableSectionTile
                                      key={section.id}
                                      section={section}
                                      hubId={hubId}
                                      renamingId={renamingId}
                                      renameValue={renameValue}
                                      setRenamingId={setRenamingId}
                                      setRenameValue={setRenameValue}
                                      handleRenameSection={handleRenameSection}
                                      handleDeleteSection={handleDeleteSection}
                                      navigate={navigate}
                                  />
                              ))}
                          </div>
                      </SortableContext>
                  </DndContext>
                )}
            </div>
        </div>
    );
}